import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Divider, Grid } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../formik/FormikTextField";
import { FormikAutocompleteSelect } from "../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { nameof } from "../../../../utils/nameof";
import { ProceedingParticipantType } from "../../../api/ClientFileApi";
import { ClaimStatusKey } from "../../claimStatus";
import { ClientSpecificParticipantDialog } from "../client-specific-participant/ClientSpecificParticipantDialog";
import { ClaimFormEditData, ClaimFormOptions, ClaimFormValues, ClaimMaxLength } from "./ClaimFormFields.types";
import { BalanceHistoryTable } from "./components/balance-history/BalanceHistoryTable";
import { CollateralSection } from "./components/collaterals/CollateralSection";
import { collateralValuesFor } from "./components/collaterals/Collaterals.utils";
import { LockedParticipantField } from "./components/locked-participant-field/LockedParticipantField";
import { ClaimParticipantSearch } from "./components/participant-search/ClaimParticipantSearch";
import { SectionHeader } from "./components/section-header/SectionHeader";

interface Props {
  formik: FormikProps<ClaimFormValues>;
  options: ClaimFormOptions;
  clientId?: string;
  editData?: ClaimFormEditData;
}

export function ClaimFormFields({ formik, options, clientId, editData }: Props) {
  const isEdit = editData !== undefined;
  const creditor = editData?.creditor;
  const representative = editData?.representative;
  const hasInactiveRepresentative = editData?.hasInactiveRepresentative ?? false;
  const [editCreditorId, setEditCreditorId] = useState<string | null>(null);
  const { t } = useTranslation();
  const {
    legalBasisOptions,
    disputedStatusOptions,
    collateralStatusOptions,
    claimEntirelyDisputedOptions,
    srvRegistrationStatusOptions,
    entryTypeOptions,
  } = options;

  const isDisputed = formik.values.disputedStatus === ClaimStatusKey.Yes;
  const showRecognizedAmount = isDisputed && formik.values.claimEntirelyDisputed === ClaimStatusKey.No;

  const onCollateralStatusChange = (status: string | null) => {
    const next = collateralValuesFor(status, formik.values);
    void formik.setFieldValue(nameof<ClaimFormValues>("collaterals"), next.collaterals);
    void formik.setFieldValue(nameof<ClaimFormValues>("expectedLossPercent"), next.expectedLossPercent);
  };

  return (
    <Grid container rowSpacing={2} columnSpacing={2}>
      <Grid size={12} pt={1}>
        {isEdit ? (
          <LockedParticipantField
            label={t("claims.form.creditor")}
            value={creditor?.displayName ?? ""}
            isClientSpecific={creditor?.isClientSpecific ?? false}
            onEdit={
              creditor?.id && creditor.isClientSpecific ? () => setEditCreditorId(creditor.id ?? null) : undefined
            }
            editAriaLabel={t("claims.form.editClientSpecificParticipant", { entity: t("claims.form.creditor") })}
          />
        ) : (
          <ClaimParticipantSearch
            formik={formik}
            participantIdField="creditorId"
            types={[ProceedingParticipantType.Creditor]}
            label={t("claims.form.creditor")}
            entityLabel={t("claims.form.creditor")}
            required
            clientId={clientId}
            onEditClientSpecific={setEditCreditorId}
          />
        )}
      </Grid>

      <Grid size={12}>
        <ClaimParticipantSearch
          formik={formik}
          participantIdField="representativeId"
          types={[ProceedingParticipantType.LawFirm, ProceedingParticipantType.DebtCollectionAgency]}
          initialDisplayName={representative?.displayName}
          initialParticipantId={representative?.id}
          initialIsClientSpecific={representative?.isClientSpecific}
          label={t("claims.form.representative")}
          entityLabel={t("claims.form.representative")}
          inactiveError={hasInactiveRepresentative ? t("claims.form.inactiveRepresentative") : undefined}
          clientId={clientId}
          onEditClientSpecific={setEditCreditorId}
        />
      </Grid>

      {editCreditorId && clientId && (
        <ClientSpecificParticipantDialog
          clientId={clientId}
          allowedTypes={[
            ProceedingParticipantType.Creditor,
            ProceedingParticipantType.LawFirm,
            ProceedingParticipantType.DebtCollectionAgency,
          ]}
          editParticipantId={editCreditorId}
          onCreated={() => {
            /* not used in edit mode */
          }}
          onClose={() => setEditCreditorId(null)}
        />
      )}

      <Grid size={12}>
        <Divider />
      </Grid>

      <Grid size={6}>
        <FormikTextField
          formik={formik}
          fieldName="caseNumber"
          label={t("claims.form.caseNumber")}
          slotProps={{ input: { inputProps: { maxLength: ClaimMaxLength.caseNumber } } }}
        />
      </Grid>

      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="legalBasis"
          label={t("claims.form.legalBasis")}
          options={legalBasisOptions}
          required
        />
      </Grid>

      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="srvRegistrationStatus"
          label={t("claims.form.srvRegistrationStatus")}
          options={srvRegistrationStatusOptions}
        />
      </Grid>

      <Grid size={6}>
        <FormikDecimalTextField
          formik={formik}
          fieldName="srvOrderNumber"
          label={t("claims.form.srvOrderNumber")}
          decimalScale={0}
          minValue={0}
          allowNegative={false}
          disableThousandSeparator
        />
      </Grid>

      <Grid size={12}>
        <Divider />
      </Grid>

      {/* Both answers are Nein for nearly every claim, so they share one row and only a Ja unfolds its section. */}
      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="disputedStatus"
          label={t("claims.form.disputedStatus")}
          options={disputedStatusOptions}
          required
        />
      </Grid>

      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="collateralStatus"
          label={t("claims.form.collateralStatus")}
          options={collateralStatusOptions}
          required
          onValueChange={onCollateralStatusChange}
        />
      </Grid>

      {isDisputed && (
        <>
          <Grid size={12}>
            <Divider />
          </Grid>

          <Grid size={12}>
            <SectionHeader>{t("claims.form.claimEntirelyDisputedSection")}</SectionHeader>
          </Grid>

          <Grid size={6}>
            <FormikAutocompleteSelect
              formik={formik}
              fieldName="claimEntirelyDisputed"
              label={t("claims.form.claimEntirelyDisputed")}
              options={claimEntirelyDisputedOptions}
              required
            />
          </Grid>
          {showRecognizedAmount && (
            <Grid size={6}>
              <FormikDecimalTextField
                formik={formik}
                fieldName="recognizedAmount"
                label={t("claims.form.recognizedAmount")}
                required
                minValue={0}
              />
            </Grid>
          )}
        </>
      )}

      <CollateralSection formik={formik} />

      <Grid size={12}>
        <Divider />
      </Grid>

      <Grid size={12}>
        <BalanceHistoryTable formik={formik} entryTypeOptions={entryTypeOptions} />
      </Grid>

      <Grid size={12}>
        <FormikTextField formik={formik} fieldName="notes" label={t("claims.form.notes")} multiline rows={3} />
      </Grid>
    </Grid>
  );
}
